/* ==========================================================================
   Fund blend guard banner (Phase 0 of Horizon / Meridian separation, 2026-09-16).

   Platform totals cover the fund of record (Horizon) only. The backend publishes every
   OTHER fund sub-account it read, and whether any of them trips the guard
   (docs/DESIGN_HORIZON_MERIDIAN_SEPARATION.md section 3; wording approved by the CIO).

   M3 Decision 7 (CIO 2026-09-25, built 2026-10-28): only money in an account that is NOT in
   the fund registry trips the guard. A registered fund's row carries `registered: true`; its
   money (Meridian's) shows as a grey line instead (CIO 2026-10-28, until the M7 fund switch
   lets these pages show Meridian).

   Tones (CIO decisions 2026-09-16 and 2026-10-28):
     red    tripped: an account not on the fund list holds money. Sticky on the backend: an
            unsure read never downgrades it; only a clean read ends it.
     amber  the guard could not be verified (or is not configured), with when it last was.
     grey   verified clear, and a registered fund other than Horizon holds money.
     none   verified clear, no other fund holds money.
   ========================================================================== */

export type FundGuardRow = {
  account?: string;
  fund?: string;
  registered?: boolean;
  NetLiquidation?: number | null;
  Cash?: number | null;
  unread?: boolean;
};

export type FundGuardPayload = {
  state?: "clear" | "tripped" | "unknown" | "unconfigured" | string;
  out_of_scope?: FundGuardRow[];
  checked_at_utc?: string;
  last_verified_at_utc?: string | null;
};

export type FundGuardNotice = { tone: "red" | "amber" | "grey"; text: string };

const usd = (n: number): string =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

const localTime = (iso: string): string | null => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(d);
};

/** The balance a funded out-of-scope account holds: net liquidation, or cash when NAV has not caught up. */
const heldAmount = (r: FundGuardRow): number => {
  const nl = typeof r.NetLiquidation === "number" ? r.NetLiquidation : 0;
  const cash = typeof r.Cash === "number" ? r.Cash : 0;
  return Math.abs(nl) >= 1 ? nl : cash;
};

const holdsMoney = (r: FundGuardRow): boolean => !r.unread && Math.abs(heldAmount(r)) >= 1;

/** Only an explicit `registered: true` counts: a row without the key (an older guard) is treated as unregistered. */
const isRegistered = (r: FundGuardRow): boolean => r.registered === true;

export const describeFundGuard = (
  p: FundGuardPayload | null | undefined,
  fmtTime: (iso: string) => string | null = localTime,
): FundGuardNotice | null => {
  if (!p) return null;
  const rows = p.out_of_scope || [];
  if (p.state === "tripped") {
    const funded = rows.filter((r) => holdsMoney(r) && !isRegistered(r));
    if (!funded.length) {
      return {
        tone: "red",
        text: "An IBKR account that is not on the fund list holds money. These pages do not include it. Check each fund's IBKR account setting in Azure.",
      };
    }
    return {
      tone: "red",
      text: funded
        .map((r) => {
          const id = r.account ? ` (${r.account})` : "";
          return `An IBKR account that is not on the fund list${id} holds ${usd(heldAmount(r))}. These pages do not include it. Check each fund's IBKR account setting in Azure.`;
        })
        .join(" "),
    };
  }
  if (p.state === "unconfigured") {
    return { tone: "amber", text: "Fund guard is not configured: NAV totals add up every fund account at IBKR." };
  }
  if (p.state === "clear") {
    const funded = rows.filter((r) => holdsMoney(r) && isRegistered(r));
    if (!funded.length) return null;
    return {
      tone: "grey",
      text: funded
        .map((r) => `${r.fund || "Another fund"}'s IBKR account holds ${usd(heldAmount(r))}. These pages show Horizon only.`)
        .join(" "),
    };
  }
  // Anything else, including an unrecognised state, is unverified: never read as clear.
  const since = p.last_verified_at_utc ? fmtTime(p.last_verified_at_utc) : null;
  return {
    tone: "amber",
    text:
      (since ? `Fund guard not verified since ${since}.` : "Fund guard not verified yet.") +
      " Figures stay Horizon-only; money in an account not on the fund list would not be flagged until IBKR can be read.",
  };
};
